import { useContext, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import AppFormSection from 'components/forms/AppFormSection'
import Tips from 'components/Tips'
import emptyChoicesToNull from 'components/forms/emptyChoicesToNull'
import { popupRefusalOf } from 'components/forms/popupRefusal'
import { refusalOf } from 'components/forms/refusal'
import { NoticesProvider } from 'components/notices/NoticesContext'
import PopupNoticesContainer from 'components/notices/PopupNoticesContainer'
import useValidations from 'hooks/useValidations'
import { applyServerErrors, clearServerErrors } from 'components/forms/serverErrors'
import schema from 'schemas/firm'
import { useNotificationContext } from 'units/notifications/NotificationContext'
import FetchContext from '../../context/fetch/FetchContext'
import ModalDialog from '../modal-dialog/ModalDialog'

const FIRMS_QUERY_KEY = ['firms']

const INITIAL_VALUES = {
    name: '',
    mailbox: '',
}

const MAILBOX_TIPS = [
    'יש להזין שם פשוט וקליט באנגלית בלבד. אפשר להשתמש גם בספרות, ואפשר להפריד בין מלים עם מקף או נקודה.',
    {
        text: 'לא ניתן לשנות את תיבת הדואר לאחר יצירת המשרד.',
        severity: 'warning',
    },
]

const CODE_FIELDS = {
    FIRM_NAME_REQUIRED: 'name',
    FIRM_MAILBOX_REQUIRED: 'mailbox',
    FIRM_MAILBOX_INVALID: 'mailbox',
    FIRM_MAILBOX_TAKEN: 'mailbox',
}

export default function FirmCreateDialog(props) {
    return (
        <NoticesProvider>
            <FirmCreateDialogContent {...props} />
        </NoticesProvider>
    )
}

function FirmCreateDialogContent({
    open = false,
    onClose,
}) {
    const navigate = useNavigate()

    const fetcher = useContext(FetchContext)

    const queryClient = useQueryClient()

    const { addNotification } = useNotificationContext()

    const formik = useRef(null)

    const { showValidations, clearValidations } = useValidations()

    const { fields } = schema.describe()

    const labelOf = (field) => fields[field]?.label

    const mutation = useMutation({
        mutationFn: (values) => {
            const sent = emptyChoicesToNull(values, fields)

            return fetcher.authenticatedFetch({
                method: 'post',
                path: '/firms',
                data: { data: sent },
            })
        },
        onSuccess: (created) => {
            queryClient.invalidateQueries({ queryKey: FIRMS_QUERY_KEY })
            navigate(`/firms/${created.id}`)
        },
        onError: (error) => {
            const { fieldErrors } = popupRefusalOf(error, {
                labelOf,
                fieldNames: Object.keys(INITIAL_VALUES),
                codeFields: CODE_FIELDS,
            })
            const { notices } = refusalOf(error, { labelOf })

            addNotification({
                type: 'error',
                content: 'יצירת המשרד נכשלה',
            })
            showValidations(notices)
            applyServerErrors(formik.current, fieldErrors)
        },
    })

    const submit = () => formik.current?.submitForm()

    const commands = [
        {
            label: 'ביטול',
            testId: 'firm-create-cancel',
            color: 'secondary',
            disabled: mutation.isPending,
            onClick: onClose,
        },
        {
            label: 'שמירה',
            testId: 'firm-create-save',
            color: 'primary',
            disabled: mutation.isPending,
            onClick: submit,
        },
    ]

    return (
        <ModalDialog
            open={open}
            title="משרד חדש"
            commands={commands}
            onClose={onClose}
        >
            <PopupNoticesContainer />
            <Formik
                innerRef={formik}
                initialValues={INITIAL_VALUES}
                validationSchema={schema.pick(Object.keys(INITIAL_VALUES))}
                onSubmit={(values) => {
                    clearServerErrors(formik.current)
                    clearValidations()
                    mutation.mutate(values)
                }}
            >
                <Form
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && !mutation.isPending) {
                            event.preventDefault()
                            formik.current?.submitForm()
                        }
                    }}
                >
                    <AppFormSection
                        section="basic"
                        fields={fields}
                    >
                        <Tips tips={MAILBOX_TIPS} />
                    </AppFormSection>
                </Form>
            </Formik>
        </ModalDialog>
    )
}
